import { useEffect, useRef } from "react";
import { AlertTriangle, Check, Info } from "lucide-react";

/**
 * The app's own dialog, replacing `window.confirm`.
 *
 * `window.confirm` was doing four jobs badly in the candidate flow. It
 * cannot be styled or themed, so a dark-theme sitting produced a stark
 * white OS box. It cannot be reached from the DOM, so nothing the e2e
 * suite does can assert on it -- `page.on('dialog')` tests that a dialog
 * appeared, not what it said. It blocks the main thread, which under a
 * running countdown is not nothing. And several browsers prefix it with
 * the tab's hostname, so a candidate is asked
 * "sonoskills.voxelradiology.com.au says: Submit your assessment?" --
 * which reads like a browser warning rather than the practice asking.
 *
 * It also could not do what step 5 needs at all: `confirm` has exactly
 * two answers, and "recorded -- next case, or review, or submit?" has
 * three.
 *
 * NOT a general modal. It is deliberately limited to a title, a body and
 * a row of actions, because everything it replaced had that shape and a
 * dialog that can contain anything becomes a second page.
 */

export type DialogTone = "info" | "danger" | "success";

export interface DialogAction {
  label: string;
  cy: string;
  onClick: () => void;
  /** `danger` for the irreversible one; `ghost` for the way out. */
  variant?: "primary" | "danger" | "ghost";
  /**
   * Takes focus when the dialog opens. Put it on the SAFE action.
   * Defaults to the last action if nothing claims it -- see below.
   */
  autoFocus?: boolean;
  /**
   * Inert, and it must be genuinely `disabled` rather than merely grey:
   * a styled-out button still fires on Enter from the keyboard, which on
   * an irreversible action is the whole failure.
   */
  disabled?: boolean;
}

const MARKS = {
  danger: AlertTriangle,
  success: Check,
  info: Info,
} as const;

export function AppDialog({
  open,
  tone = "info",
  title,
  body,
  actions,
  onDismiss,
  cy = "app-dialog",
}: {
  open: boolean;
  tone?: DialogTone;
  title: string;
  body?: React.ReactNode;
  actions: DialogAction[];
  /**
   * Escape, and a click on the backdrop. Omit ONLY for a dialog with no
   * safe way out -- every dialog here has one, so every one passes it.
   */
  onDismiss?: () => void;
  cy?: string;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Focus moves INTO the dialog on open, and lands on the action marked
  // safe rather than on the first button in the row. The destructive
  // action is usually first (it is the one the sentence is about), and
  // focusing it would make Enter -- the key somebody hits reflexively --
  // submit an assessment.
  useEffect(() => {
    if (!open) return;
    const node = cardRef.current?.querySelector<HTMLButtonElement>(
      "button[data-autofocus='true']",
    );
    (node ?? cardRef.current?.querySelector("button"))?.focus();
  }, [open]);

  useEffect(() => {
    if (!open || !onDismiss) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onDismiss();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onDismiss]);

  if (!open) return null;

  const Mark = MARKS[tone];
  // If nothing claims focus, the LAST action gets it: the rows below are
  // written destructive-first, safe-last.
  const focusIndex = actions.findIndex((a) => a.autoFocus);
  const focused = focusIndex === -1 ? actions.length - 1 : focusIndex;

  return (
    <div
      className="sk-dialog-backdrop"
      data-cy={`${cy}-backdrop`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss?.();
      }}
    >
      <div
        ref={cardRef}
        data-cy={cy}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${cy}-title`}
        className="sk-dialog"
      >
        <span className="sk-dialog-mark" data-tone={tone} aria-hidden>
          <Mark className="h-5 w-5" />
        </span>
        <h2 id={`${cy}-title`}>{title}</h2>
        {typeof body === "string" ? <p>{body}</p> : body}
        <div className="sk-dialog-actions">
          {actions.map((a, i) => (
            <button
              key={a.cy}
              type="button"
              data-cy={a.cy}
              data-autofocus={i === focused ? "true" : undefined}
              disabled={a.disabled}
              onClick={a.onClick}
              className={[
                "sk-btn",
                a.variant === "danger"
                  ? "sk-btn-danger"
                  : a.variant === "ghost"
                    ? "sk-btn-ghost"
                    : a.variant === "primary"
                      ? "sk-btn-primary"
                      : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
